(() => {
  const PRODUCT_INFO_SELECTOR = 'product-info';
  const AOV_WRAPPER_SELECTOR = '.product-form__app-block-wrapper--volume-discount';
  const CONTENT_SELECTOR = '.product-form__app-block-content';
  const MIN_CONTENT_HEIGHT = 80;
  /** Keep the placeholder briefly visible without making the CTA feel delayed. */
  const MIN_SKELETON_MS = 200;

  const isDesignMode = () =>
    document.documentElement.hasAttribute('data-shopify-editor-mode') ||
    document.body.classList.contains('shopify-design-mode');

  const getFallbackMs = () => {
    let fallback = 500;
    const connection = navigator.connection && navigator.connection.effectiveType;

    if (connection && /2g|3g/.test(connection)) {
      fallback += 400;
    }

    return fallback;
  };

  const hasUsefulAovContent = (content) => {
    if (!content) return false;

    const height = content.scrollHeight || content.offsetHeight;
    if (height < MIN_CONTENT_HEIGHT) return false;

    const marker = content.querySelector('iframe, [class*="aov" i], [id*="aov" i], [data-aov], [class*="volume" i]');
    if (marker) return true;

    if (content.children.length > 2) return true;

    const hasOptionLikeElements = content.querySelector(
      'button, [role="button"], a[href], input, [class*="option" i], [class*="tier" i], [class*="discount" i]'
    );
    return Boolean(hasOptionLikeElements);
  };

  const setupProductInfo = (productInfo) => {
    if (!productInfo || productInfo.dataset.aovReadyInitialized === 'true') return;

    const wrappers = Array.from(productInfo.querySelectorAll(AOV_WRAPPER_SELECTOR));
    if (wrappers.length === 0) return;

    productInfo.dataset.aovReadyInitialized = 'true';

    const readyWrappers = new Set();
    const observers = [];
    let timeoutId;
    let released = false;
    const startTime = Date.now();

    const markWrapperReady = (wrapper) => {
      readyWrappers.add(wrapper);
      wrapper.classList.add('is-ready');
    };

    const disconnectAll = () => {
      observers.forEach((observer) => observer.disconnect());
      observers.length = 0;
    };

    const releaseCta = () => {
      if (released) return;
      released = true;
      productInfo.classList.add('aov-volume-ready');
      wrappers.forEach(markWrapperReady);
      clearTimeout(timeoutId);
      disconnectAll();
    };

    const elapsedMs = () => Date.now() - startTime;
    const skeletonMinTimeReached = () => elapsedMs() >= MIN_SKELETON_MS;

    const maybeRelease = () => {
      if (released) return;
      if (!skeletonMinTimeReached()) return;

      wrappers.forEach((wrapper) => {
        if (readyWrappers.has(wrapper)) return;
        const content = wrapper.querySelector(CONTENT_SELECTOR);
        if (hasUsefulAovContent(content)) {
          markWrapperReady(wrapper);
        }
      });

      if (readyWrappers.size === wrappers.length) {
        releaseCta();
      }
    };

    if (isDesignMode()) {
      timeoutId = setTimeout(releaseCta, 500);
      return;
    }

    timeoutId = setTimeout(releaseCta, getFallbackMs());

    wrappers.forEach((wrapper) => {
      const content = wrapper.querySelector(CONTENT_SELECTOR);
      if (!content) return;

      const observer = new MutationObserver(() => {
        maybeRelease();
      });

      observer.observe(content, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });

      observers.push(observer);
    });

    maybeRelease();
  };

  const init = (root = document) => {
    root.querySelectorAll(PRODUCT_INFO_SELECTOR).forEach(setupProductInfo);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target || document);
  });
})();
